import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, ScrollView, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { Feather } from "@expo/vector-icons";
import type { TranslationKeys } from "@chinese-vendor/i18n";

import { Modal } from "@/components/ui/Modal";
import { AppText } from "@/components/ui/AppText";
import { SearchInput } from "@/components/ui/SearchInput";
import { Checkbox } from "@/components/ui/Checkbox";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useI18n } from "@/context/I18nContext";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MultiSelectPickerItem {
  id: string;
  name: string;
}

export interface MultiSelectPickerModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** Modal header title (i18n key). */
  title: TranslationKeys;
  /** Modal header subtitle (i18n key). */
  subtitle?: TranslationKeys;
  /** Left-panel section heading, e.g. "All Industries". */
  allSectionLabel: TranslationKeys;
  /** Right-panel section heading, e.g. "Selected Industries". */
  selectedSectionLabel: TranslationKeys;
  /**
   * i18n key used to show the count of selected items.
   * Must contain a `{count}` interpolation token.
   */
  selectedCountKey: TranslationKeys;
  /** Placeholder text for the search input. */
  searchPlaceholder: TranslationKeys;
  /** Full list of available items (API-loaded, names already localised). */
  items: MultiSelectPickerItem[];
  /** Initially-selected ids; reset to this value each time the modal opens. */
  selectedIds: string[];
  /** Called on "Save Selection" with the new set of selected ids. */
  onSave: (selectedIds: string[]) => void;
  /** Shows a loading spinner in the items list while true. */
  isLoading?: boolean;
  /**
   * When provided, a language-toggle button is rendered next to the search bar.
   * The parent decides how to handle the locale change (re-fetch, etc.).
   */
  onLocaleToggle?: () => void;
  /** Label for the language-toggle button (i18n key). */
  localeToggleLabel?: TranslationKeys;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const MultiSelectPickerModal = memo(function MultiSelectPickerModal({
  visible,
  onDismiss,
  title,
  subtitle,
  allSectionLabel,
  selectedSectionLabel,
  selectedCountKey,
  searchPlaceholder,
  items,
  selectedIds,
  onSave,
  isLoading = false,
  onLocaleToggle,
  localeToggleLabel,
}: MultiSelectPickerModalProps) {
  const { t, isRTL } = useI18n();
  const { height: windowHeight } = useWindowDimensions();

  // ── Local state ────────────────────────────────────────────────────────────
  const [pendingIds, setPendingIds] = useState<string[]>(selectedIds);
  const [query, setQuery] = useState("");

  // Reset every time the modal opens so edits don't bleed across sessions.
  useEffect(() => {
    if (visible) {
      setPendingIds([...selectedIds]);
      setQuery("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ── Derived data ───────────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    if (!query.trim()) return items;
    const lower = query.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(lower));
  }, [items, query]);

  const selectedItems = useMemo(
    () => items.filter((item) => pendingIds.includes(item.id)),
    [items, pendingIds]
  );

  const isAllSelected =
    filteredItems.length > 0 && filteredItems.every((item) => pendingIds.includes(item.id));

  // ── Handlers ───────────────────────────────────────────────────────────────
  const toggleItem = useCallback((id: string) => {
    setPendingIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  }, []);

  const toggleAll = useCallback(() => {
    const filteredIds = filteredItems.map((i) => i.id);
    if (isAllSelected) {
      setPendingIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setPendingIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  }, [filteredItems, isAllSelected]);

  const handleSave = useCallback(() => {
    onSave(pendingIds);
    onDismiss();
  }, [onSave, pendingIds, onDismiss]);

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footer = (
    <View className={`flex-row justify-end gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
      <Button variant="ghost" label="common.cancel" onPress={onDismiss} />
      <Button
        variant="primary"
        label="multiSelectPicker.saveSelection"
        onPress={handleSave}
        disabled={pendingIds.length === 0}
      />
    </View>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      title={title}
      subtitle={subtitle}
      closeOnBackdrop={false}
      useScrollView={false}
      contentContainerProps={{ className: "max-w-4xl", style: { height: windowHeight * 0.85 } }}
      contentProps={{ className: " flex-1" }}
      footer={footer}
    >
      <View className="flex-1 gap-4">
        {/* ── Search + locale toggle ───────────────────────────────────── */}
        <View
          className={`flex-row items-center flex-wrap gap-4 shrink-0 ${isRTL ? "flex-row-reverse" : ""}`}
        >
          <View className="flex-1 min-w-full sm:min-w-60">
            <SearchInput
              value={query}
              onChangeText={setQuery}
              placeholder={searchPlaceholder}
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="search"
            />
          </View>

          {onLocaleToggle && localeToggleLabel && (
            <TouchableOpacity
              onPress={onLocaleToggle}
              className="bg-[#1f6cff] rounded px-4 py-[10px] shrink-0"
              accessibilityRole="button"
              accessibilityLabel={t(localeToggleLabel)}
              activeOpacity={0.8}
            >
              <AppText weight="semibold" className="text-white text-base">
                {t(localeToggleLabel)}
              </AppText>
            </TouchableOpacity>
          )}
        </View>

        {/* ── Two-column body ──────────────────────────────────────────── */}
        <View
          className={`flex-1 flex-col tablet:flex-row border border-stroke-50 rounded-xl overflow-hidden ${isRTL ? "flex-row-reverse" : ""}`}
        >
          {/* ── Left: all items ──────────────────────────────────────── */}
          <View className="flex-[3] border-b tablet:border-b-0 tablet:border-r border-stroke-50 p-4 gap-4">
            {/* Section header */}
            <View
              className={`flex-row items-center justify-between py-2 ${isRTL ? "flex-row-reverse" : ""}`}
            >
              <AppText weight="semibold" className="text-lg text-dark-text-50">
                {t(allSectionLabel)}
              </AppText>
              <TouchableOpacity
                onPress={toggleAll}
                className={`flex-row items-center gap-2 px-4 ${isRTL ? "flex-row-reverse" : ""}`}
                accessibilityRole="button"
              >
                <AppText weight="medium" className="text-base text-dark-text-10">
                  {t("multiSelectPicker.selectAll")}
                </AppText>
                <Checkbox
                  checked={isAllSelected}
                  onChange={toggleAll}
                  testID="multiselect-select-all"
                />
              </TouchableOpacity>
            </View>

            {/* Items list inside a bordered inner box */}
            <View className="flex-1 border border-stroke-50 rounded-lg overflow-hidden">
              {isLoading ? (
                <View className="flex-1 items-center justify-center py-8">
                  <AppText className="text-base text-supporting-text">
                    {t("common.loading")}
                  </AppText>
                </View>
              ) : (
                <FlatList
                  data={filteredItems}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => {
                    const checked = pendingIds.includes(item.id);
                    return (
                      <TouchableOpacity
                        onPress={() => toggleItem(item.id)}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked }}
                        accessibilityLabel={item.name}
                        className={`flex-row items-center p-4 border-b border-stroke-50 bg-white ${
                          isRTL ? "flex-row-reverse" : ""
                        }`}
                        activeOpacity={0.7}
                      >
                        <AppText
                          weight="medium"
                          className="flex-1 text-base text-supporting-text-50 me-3"
                          numberOfLines={1}
                        >
                          {item.name}
                        </AppText>
                        <Checkbox checked={checked} onChange={() => toggleItem(item.id)} />
                      </TouchableOpacity>
                    );
                  }}
                  ListEmptyComponent={
                    <View className="items-center py-8">
                      <AppText className="text-base text-dark-text-10">
                        {t("multiSelectPicker.noResults")}
                      </AppText>
                    </View>
                  }
                  keyboardShouldPersistTaps="handled"
                />
              )}
            </View>
          </View>

          {/* ── Right: selected items ─────────────────────────────────── */}
          <View className="flex-[2] p-4 gap-3 bg-stroke-10">
            <AppText weight="semibold" className="text-lg text-dark-text-50">
              {t(selectedSectionLabel)}
            </AppText>

            {/* Count info row */}
            <View className={`flex-row items-center gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
              <Icon as={Feather} name="info" size={14} className="text-dark-text-10 shrink-0" />
              <AppText className="text-sm text-dark-text-10 flex-1">
                {t(selectedCountKey, { count: pendingIds.length })}
              </AppText>
            </View>

            {/* Selected items list */}
            <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
              {selectedItems.map((item) => (
                <View
                  key={item.id}
                  className={`flex-row items-center justify-between py-3 border-b border-stroke-50 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  <AppText className="flex-1 text-base text-dark-text-50 me-3" numberOfLines={1}>
                    {item.name}
                  </AppText>
                  <TouchableOpacity
                    onPress={() => toggleItem(item.id)}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityRole="button"
                    accessibilityLabel={`${t("multiSelectPicker.remove")} ${item.name}`}
                  >
                    <AppText weight="medium" className="text-sm text-error">
                      × {t("multiSelectPicker.remove")}
                    </AppText>
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </View>
    </Modal>
  );
});
